import { useState, useRef } from "react";

const STATIC = {
  bg: "#0e0e0d", surface: "#141412", card: "#1a1a18", border: "#242220",
  text: "#edeae2", muted: "#8a8680", dim: "#585450",
  green: "#5ab870", greenBg: "#0c1a0e", greenBorder: "#1e3d22",
  blue: "#6aaee0", blueBg: "#0d1822", blueBorder: "#1c3148",
  red: "#e06060", redBg: "#1a0c0c", redBorder: "#3d1e1e",
  amber: "#e0a030", amberBg: "#1a1000", amberBorder: "#3d2800",
};

const inputBase = {
  width: "100%", maxWidth: "100%", padding: "13px 15px", background: "#111",
  border: "1px solid #242424", borderRadius: 8, color: "#e8e0d0",
  fontSize: 15, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  boxSizing: "border-box", outline: "none", WebkitAppearance: "none",
};

function Lbl({ children, color }) {
  return <div style={{ fontSize: 13, color: color || STATIC.muted, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 10 }}>{children}</div>;
}
function Fld({ label, children, color }) {
  return <div style={{ marginBottom: 22 }}><Lbl color={color}>{label}</Lbl>{children}</div>;
}
function Chips({ options, value, onChange, orange, accentRgb, disabled }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map(o => (
        <button key={o} onClick={() => !disabled && onChange(o)} style={{
          padding: "10px 16px", borderRadius: 8, fontFamily: "inherit", fontSize: 14,
          cursor: disabled ? "not-allowed" : "pointer",
          border: `1px solid ${value === o ? orange : "#242220"}`,
          background: value === o ? `rgba(${accentRgb},0.08)` : "#0f0f0f",
          color: value === o ? orange : STATIC.muted,
          opacity: disabled ? 0.6 : 1,
        }}>{o}</button>
      ))}
    </div>
  );
}

function SectionHead({ children }) {
  return <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 14, paddingBottom: 8, borderBottom: `1px solid ${STATIC.border}`, marginTop: 4 }}>{children}</div>;
}

function confColor(s) {
  if (s >= 80) return STATIC.green;
  if (s >= 60) return STATIC.amber;
  return STATIC.red;
}
function confBg(s) {
  if (s >= 80) return STATIC.greenBg;
  if (s >= 60) return STATIC.amberBg;
  return STATIC.redBg;
}
function confBorder(s) {
  if (s >= 80) return STATIC.greenBorder;
  if (s >= 60) return STATIC.amberBorder;
  return STATIC.redBorder;
}

// Normalise AI-detected instrument string to chip option values
function normaliseInstrument(raw) {
  if (!raw) return "Bass Guitar";
  const r = raw.toLowerCase();
  if (r.includes("bass")) return "Bass Guitar";          // bass checked first — "electric bass", "acoustic bass" etc. all map here
  if (r.includes("acoustic")) return "Acoustic Guitar";
  if (r.includes("electric") || r.includes("guitar")) return "Electric Guitar";
  return "Bass Guitar";
}

// Normalise AI-detected condition string to chip option values
function normaliseCondition(raw) {
  if (!raw) return "";
  const r = raw.toLowerCase();
  if (r.includes("excellent")) return "Excellent";
  if (r.includes("good")) return "Good";
  if (r.includes("fair")) return "Fair";
  if (r.includes("poor")) return "Poor";
  return "";
}

export default function AddInstrumentPage({ evalData, C, accentRgb, userId, onSave, onBack, defaultType }) {
  const orange = C?.orange || "#E8621A";
  const rgb = accentRgb || "232,98,26";

  const ev = evalData || null;
  const mo = ev?.model_overview || {};
  const hw = ev?.hardware_overview || {};
  const cond = ev?.condition || {};
  const sp = ev?.setup_profile || {};
  const mv = ev?.market_value || {};
  const det = ev?.detected || {};

  const [form, setForm] = useState({
    instrument_type: defaultType || "my_gear",
    brand: mo.make || "",
    model: det.model || "",
    instrument: normaliseInstrument(det.instrument),
    year: det.year || mo.year || "",
    origin: det.origin || mo.origin || "",
    serial: det.serial || "",
    bridge: det.bridge || hw.bridge || "",
    strings: det.strings || "",
    string_gauge: "",
    condition: normaliseCondition(cond.overall),
    // Purchase Details
    purchase_price: "",
    purchase_date: "",
    new_or_used: "",          // "New" | "Used"
    receipt_url: "",
    warranty_duration: "",
    warranty_expired: "",     // "Active" | "Expired"
    notes: "",
    listing_url: ev?.listing_url || "",
    special_details: ev?.special_details || "",
    // Flip Details
    target_price: "",
    estimated_parts: "",
    estimated_hours: "",
    // Mates Gear
    owner_name: "",
    owner_contact: "",
    job_description: "",
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const [imagePreviews, setImagePreviews] = useState(ev?.imageUrls || []);
  const [imageUrls, setImageUrls] = useState(ev?.imageUrls || []);
  const [uploading, setUploading] = useState(false);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const fileRef = useRef(null);
  const receiptRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [aiOpen, setAiOpen] = useState(true);

  const handleFiles = async (files) => {
    const arr = Array.from(files).slice(0, 8 - imagePreviews.length);
    if (!arr.length) return;
    setImagePreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setUploading(true);
    try {
      const fd = new FormData();
      arr.forEach(f => fd.append("images", f));
      fd.append("mode", "instruments");
      const r = await fetch("/api/upload-images", { method: "POST", body: fd });
      const d = await r.json();
      if (d.imageUrls?.length) setImageUrls(prev => [...prev, ...d.imageUrls]);
    } catch(e) { console.error("Upload error:", e); }
    finally { setUploading(false); }
  };

  const removeImg = (idx) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
    setImageUrls(prev => prev.filter((_, i) => i !== idx));
  };

  const handleReceiptFile = async (files) => {
    const file = Array.from(files)[0];
    if (!file) return;
    setReceiptUploading(true);
    try {
      const fd = new FormData();
      fd.append("images", file);
      fd.append("mode", "instruments");
      const r = await fetch("/api/upload-images", { method: "POST", body: fd });
      const d = await r.json();
      if (d.imageUrls?.[0]) set("receipt_url", d.imageUrls[0]);
    } catch(e) { console.error("Receipt upload error:", e); }
    finally { setReceiptUploading(false); }
  };

  const handleSave = async () => {
    if (!form.model.trim()) { setSaveError("Make / Model is required."); return; }
    setSaveError("");
    setIsSaving(true);
    try {
      const heroUrl = imageUrls[0] || "";
      const matesNotes = form.instrument_type === "servicing"
        ? [form.owner_name && `Owner: ${form.owner_name}`, form.owner_contact && `Contact: ${form.owner_contact}`, form.job_description && `Job: ${form.job_description}`].filter(Boolean).join("\n")
        : "";

      const fd = new FormData();
      fd.append("user", userId);
      const fields = {
        instrument_type: form.instrument_type,
        brand: form.brand,
        model: form.model,
        instrument: form.instrument,
        year: form.year,
        origin: form.origin,
        serial: form.serial,
        bridge: form.bridge,
        strings: form.strings,
        string_gauge: form.string_gauge,
        condition: form.condition,
        notes: form.notes || matesNotes,
        listing_url: form.listing_url,
        special_details: form.special_details,
        purchase_price: form.purchase_price,
        purchase_date: form.purchase_date,
        new_or_used: form.new_or_used,
        receipt_url: form.receipt_url,
        warranty_duration: form.warranty_duration,
        warranty_expired: form.warranty_expired,
        status: "active",
        existing_image_urls: imageUrls.length ? JSON.stringify(imageUrls) : "",
        hero_image_url: heroUrl,
        evaluate_result: ev ? JSON.stringify(ev) : "",
        confidence_score: ev?.confidence_score != null ? String(ev.confidence_score) : "",
        intent: ev?.intent || "",
        condition_flags: cond.flags?.length ? JSON.stringify(cond.flags) : "",
        market_value_low: mv?.fair_range_low != null ? String(mv.fair_range_low) : "",
        market_value_high: mv?.fair_range_high != null ? String(mv.fair_range_high) : "",
        market_currency: mv?.currency || "",
        action_range: sp?.recommended_action_range || "",
        ...(form.instrument_type === "flip" ? {
          target_price: form.target_price,
          estimated_parts: form.estimated_parts,
          estimated_hours: form.estimated_hours,
        } : {}),
      };
      Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") fd.append(k, String(v)); });

      const resp = await fetch("/api/catalogue", { method: "POST", body: fd });
      const saved = await resp.json();
      if (saved?.id) {
        onSave(saved, form.instrument_type);
      } else {
        setSaveError(saved?.error || "Save failed — please try again.");
      }
    } catch(e) {
      setSaveError("Save failed — please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const typeLabel = { my_gear: "My Gear", flip: "Flip", servicing: "Mates Gear" };
  const showPurchase = form.instrument_type !== "servicing";
  const showFlip = form.instrument_type === "flip";
  const showMates = form.instrument_type === "servicing";
  const locked = isSaving;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 20px 120px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: STATIC.dim, fontFamily: "inherit", fontSize: 13, cursor: "pointer", padding: "0 0 16px", display: "flex", alignItems: "center", gap: 6 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        Instruments
      </button>

      <div style={{ fontSize: 22, fontWeight: 700, color: STATIC.text, marginBottom: 6 }}>Add Instrument</div>
      <p style={{ color: STATIC.muted, margin: "0 0 24px", fontSize: 14 }}>
        {ev ? "Review the AI evaluation below, correct any details, then save." : "Fill in the details to catalogue this instrument."}
      </p>

      {/* AI Summary Panel */}
      {ev && (
        <div style={{ marginBottom: 24, background: STATIC.surface, border: `1px solid ${STATIC.border}`, borderRadius: 14, overflow: "hidden" }}>
          <button onClick={() => setAiOpen(p => !p)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: orange, textTransform: "uppercase", letterSpacing: "0.09em" }}>AI Evaluation Summary</span>
              <span style={{ fontSize: 11, color: STATIC.dim }}>— review before saving</span>
              {ev.confidence_score != null && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: confBg(ev.confidence_score), border: `1px solid ${confBorder(ev.confidence_score)}` }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: confColor(ev.confidence_score) }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: confColor(ev.confidence_score) }}>{ev.confidence_score}%</span>
                </div>
              )}
            </div>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={STATIC.dim} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: aiOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {aiOpen && (
            <div style={{ borderTop: `1px solid ${STATIC.border}`, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
              {(mo.make || mo.era_notes) && (
                <div>
                  <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Model</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: STATIC.text }}>{[mo.make, mo.model].filter(Boolean).join(" ")}{(mo.year || mo.origin) ? <span style={{ fontSize: 12, color: STATIC.muted, fontWeight: 400, marginLeft: 8 }}>{[mo.year, mo.origin].filter(Boolean).join(" · ")}</span> : ""}</div>
                  {mo.era_notes && <div style={{ fontSize: 13, color: STATIC.muted, lineHeight: 1.6, marginTop: 4 }}>{mo.era_notes}</div>}
                </div>
              )}
              {(hw.pickups || hw.tuners || hw.nut_material || hw.notable_specs) && (
                <div>
                  <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Hardware</div>
                  {[["Pickups", hw.pickups], ["Tuners", hw.tuners], ["Nut", hw.nut_material], ["Notable", hw.notable_specs]].filter(([,v]) => v).map(([k, v]) => (
                    <div key={k} style={{ fontSize: 13, color: STATIC.muted, marginBottom: 2 }}><span style={{ color: STATIC.dim, marginRight: 6 }}>{k}</span>{v}</div>
                  ))}
                </div>
              )}
              {cond.flags?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Condition Flags</div>
                  {cond.flags.map((f, i) => <div key={i} style={{ display: "flex", gap: 6, fontSize: 13, color: STATIC.muted, marginBottom: 3 }}><span style={{ color: STATIC.amber }}>⚑</span>{f}</div>)}
                </div>
              )}
              {(sp.string_gauges || sp.known_quirks || sp.recommended_action_range) && (
                <div>
                  <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Setup Profile</div>
                  {[["Gauges", sp.string_gauges], ["Action", sp.recommended_action_range], ["Quirks", sp.known_quirks]].filter(([,v]) => v).map(([k, v]) => (
                    <div key={k} style={{ fontSize: 13, color: STATIC.muted, marginBottom: 2 }}><span style={{ color: STATIC.dim, marginRight: 6 }}>{k}</span>{v}</div>
                  ))}
                </div>
              )}
              {mv?.fair_range_low != null && mv?.fair_range_high != null && (
                <div>
                  <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Market Value</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: orange }}>{mv.currency} {mv.fair_range_low}–{mv.fair_range_high}</div>
                  {mv.suggested_offer != null && <div style={{ fontSize: 12, color: STATIC.muted, marginTop: 2 }}>Suggested offer: {mv.currency} {mv.suggested_offer}</div>}
                </div>
              )}
              {ev.seller_questions?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: STATIC.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Seller Questions</div>
                  {ev.seller_questions.map((q, i) => <div key={i} style={{ fontSize: 13, color: STATIC.muted, marginBottom: 3, paddingLeft: 10, position: "relative" }}><span style={{ position: "absolute", left: 0, color: orange }}>·</span>{q}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form body — locked while saving */}
      <div style={{ pointerEvents: locked ? "none" : "auto", opacity: locked ? 0.55 : 1, transition: "opacity 0.2s" }}>

        {/* ── Section: Photos (top) ── */}
        <SectionHead>Photos</SectionHead>
        <div style={{ fontSize: 12, color: STATIC.dim, marginBottom: 10 }}>Up to 8 — headstock, neck, body, bridge, any damage</div>
        {imagePreviews.length === 0 ? (
          <button onClick={() => fileRef.current?.click()} style={{
            background: STATIC.surface, border: `1.5px dashed ${STATIC.border}`, borderRadius: 12, padding: 20,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            cursor: "pointer", width: "100%", fontFamily: "inherit", marginBottom: 24, boxSizing: "border-box",
          }}>
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke={STATIC.muted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.45 }}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            <span style={{ fontSize: 14, fontWeight: 600, color: STATIC.muted }}>Add Photos</span>
            <span style={{ fontSize: 11, color: STATIC.dim }}>Opens your photo library, camera or files</span>
          </button>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 24 }}>
            {imagePreviews.map((p, i) => (
              <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "visible", background: STATIC.card, border: `1px solid ${STATIC.border}` }}>
                <img src={p} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                {i === 0 && <div style={{ position: "absolute", bottom: 4, left: 4, fontSize: 9, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,0.65)", borderRadius: 4, padding: "2px 5px" }}>HERO</div>}
                <button onClick={() => removeImg(i)} style={{ position: "absolute", top: -4, right: -4, background: STATIC.red, border: "none", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", cursor: "pointer", padding: 0 }}>×</button>
              </div>
            ))}
            {imagePreviews.length < 8 && (
              <button onClick={() => fileRef.current?.click()} style={{ aspectRatio: "1", background: STATIC.surface, border: `1.5px dashed ${STATIC.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: STATIC.dim, cursor: "pointer", fontFamily: "inherit" }}>+</button>
            )}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
        {uploading && <div style={{ fontSize: 12, color: STATIC.blue, marginBottom: 12 }}>Uploading photos…</div>}

        {/* ── Section: Identity ── */}
        <SectionHead>Identity</SectionHead>
        <Fld label="Category" color={orange}>
          <Chips options={["My Gear", "Flip", "Mates Gear"]} value={{ my_gear: "My Gear", flip: "Flip", servicing: "Mates Gear" }[form.instrument_type] || "My Gear"} onChange={v => set("instrument_type", { "My Gear": "my_gear", "Flip": "flip", "Mates Gear": "servicing" }[v] || "my_gear")} orange={orange} accentRgb={rgb} disabled={locked} />
        </Fld>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Brand"><input value={form.brand} onChange={e => set("brand", e.target.value)} placeholder="e.g. Fender, Warwick" style={inputBase} disabled={locked} /></Fld>
          <Fld label="Model *"><input value={form.model} onChange={e => set("model", e.target.value)} placeholder="e.g. Jazz Bass MIM" style={inputBase} disabled={locked} /></Fld>
        </div>
        <Fld label="Instrument Type" color={orange}>
          <Chips options={["Bass Guitar", "Electric Guitar", "Acoustic Guitar"]} value={form.instrument} onChange={v => set("instrument", v)} orange={orange} accentRgb={rgb} disabled={locked} />
        </Fld>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Fld label="Year"><input value={form.year} onChange={e => set("year", e.target.value)} placeholder="e.g. 2019" style={inputBase} disabled={locked} /></Fld>
          <Fld label="Origin"><input value={form.origin} onChange={e => set("origin", e.target.value)} placeholder="e.g. Japan" style={inputBase} disabled={locked} /></Fld>
          <Fld label="Serial"><input value={form.serial} onChange={e => set("serial", e.target.value)} placeholder="e.g. Z1234567" style={inputBase} disabled={locked} /></Fld>
        </div>

        {/* ── Section: Hardware ── */}
        <SectionHead>Hardware</SectionHead>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Bridge"><input value={form.bridge} onChange={e => set("bridge", e.target.value)} placeholder="e.g. Mono-rail" style={inputBase} disabled={locked} /></Fld>
          <Fld label="Strings"><input value={form.strings} onChange={e => set("strings", e.target.value)} placeholder="e.g. 4-String" style={inputBase} disabled={locked} /></Fld>
        </div>
        <Fld label="String Gauge"><input value={form.string_gauge} onChange={e => set("string_gauge", e.target.value)} placeholder="e.g. DR Black Beauty .045–.105" style={inputBase} disabled={locked} /></Fld>
        <Fld label="Condition" color={orange}>
          <Chips options={["Excellent", "Good", "Fair", "Poor"]} value={form.condition} onChange={v => set("condition", v)} orange={orange} accentRgb={rgb} disabled={locked} />
        </Fld>

        {/* ── Section: Purchase Details ── */}
        {showPurchase && (
          <>
            <SectionHead>Purchase Details</SectionHead>

            {/* New / Used toggle */}
            <Fld label="New or Used">
              <Chips options={["New", "Used"]} value={form.new_or_used} onChange={v => set("new_or_used", v)} orange={orange} accentRgb={rgb} disabled={locked} />
            </Fld>

            {/* Price + Date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Fld label="Price Paid ($)"><input value={form.purchase_price} onChange={e => set("purchase_price", e.target.value)} type="number" placeholder="e.g. 850" style={inputBase} disabled={locked} /></Fld>
              <Fld label="Date Purchased"><input value={form.purchase_date} onChange={e => set("purchase_date", e.target.value)} type="date" style={{ ...inputBase, maxWidth: "100%" }} disabled={locked} /></Fld>
            </div>

            {/* Receipt, Warranty — only shown when New */}
            {form.new_or_used === "New" && (() => {
              // M3: auto-calculate warranty status
              const warrantyBadge = (() => {
                if (!form.purchase_date || !form.warranty_duration) return null;
                const months = parseInt(form.warranty_duration, 10);
                if (!months) return null;
                const purchase = new Date(form.purchase_date);
                const expiry = new Date(purchase);
                expiry.setMonth(expiry.getMonth() + months);
                const now = new Date();
                const totalMs = expiry - purchase;
                const elapsedMs = now - purchase;
                const remainingDays = (expiry - now) / (1000 * 60 * 60 * 24);
                const elapsedPct = totalMs > 0 ? (elapsedMs / totalMs) * 100 : 100;
                const expiryStr = expiry.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
                if (now > expiry) return { label: "Expired", sub: `Expired ${expiryStr}`, color: STATIC.red, bg: STATIC.redBg, border: STATIC.redBorder };
                if (remainingDays < 62 || elapsedPct >= 75) return { label: "Expires Soon", sub: `Expires ${expiryStr}`, color: STATIC.amber, bg: STATIC.amberBg, border: STATIC.amberBorder };
                return { label: "In Warranty", sub: `Expires ${expiryStr}`, color: STATIC.green, bg: STATIC.greenBg, border: STATIC.greenBorder };
              })();
              return (
                <>
                  <Fld label="Receipt">
                    {form.receipt_url ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: STATIC.surface, border: `1px solid ${STATIC.border}`, borderRadius: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={STATIC.green} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span style={{ flex: 1, fontSize: 13, color: STATIC.muted }}>Receipt uploaded</span>
                        <button onClick={() => set("receipt_url", "")} style={{ background: "none", border: "none", color: STATIC.dim, cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
                      </div>
                    ) : (
                      <button onClick={() => receiptRef.current?.click()} style={{
                        width: "100%", padding: "13px 15px", background: STATIC.surface, border: `1px dashed ${STATIC.border}`,
                        borderRadius: 8, color: STATIC.muted, fontFamily: "inherit", fontSize: 14, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 10, boxSizing: "border-box",
                      }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                        {receiptUploading ? "Uploading…" : "Upload receipt (photo or PDF)"}
                      </button>
                    )}
                    <input ref={receiptRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => { handleReceiptFile(e.target.files); e.target.value = ""; }} />
                  </Fld>

                  {/* M2: Warranty Duration dropdown */}
                  <Fld label="Warranty Duration">
                    <select value={form.warranty_duration} onChange={e => set("warranty_duration", e.target.value)} style={{ ...inputBase, appearance: "none", WebkitAppearance: "none" }} disabled={locked}>
                      <option value="">Select duration…</option>
                      <option value="12">12 months (1 year)</option>
                      <option value="24">24 months (2 years)</option>
                      <option value="36">36 months (3 years)</option>
                      <option value="48">48 months (4 years)</option>
                      <option value="60">60 months (5 years)</option>
                    </select>
                  </Fld>

                  {/* M3: Auto-calculated warranty badge */}
                  {warrantyBadge && (
                    <div style={{ marginBottom: 22 }}>
                      <Lbl>Warranty Status</Lbl>
                      <div style={{ display: "inline-flex", flexDirection: "column", gap: 3, padding: "8px 14px", borderRadius: 8, background: warrantyBadge.bg, border: `1px solid ${warrantyBadge.border}` }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: warrantyBadge.color }}>{warrantyBadge.label}</div>
                        <div style={{ fontSize: 11, color: warrantyBadge.color, opacity: 0.75 }}>{warrantyBadge.sub}</div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <Fld label="Notes"><textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Mods, condition, history…" rows={2} style={{ ...inputBase, resize: "vertical" }} disabled={locked} /></Fld>
          </>
        )}

        {/* ── Section: Flip Details ── */}
        {showFlip && (
          <>
            <SectionHead>Flip Details</SectionHead>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Fld label="Target Sell Price ($)"><input value={form.target_price} onChange={e => set("target_price", e.target.value)} type="number" placeholder="e.g. 1200" style={inputBase} disabled={locked} /></Fld>
              <Fld label="Est. Parts ($)"><input value={form.estimated_parts} onChange={e => set("estimated_parts", e.target.value)} type="number" placeholder="e.g. 40" style={inputBase} disabled={locked} /></Fld>
              <Fld label="Est. Hours"><input value={form.estimated_hours} onChange={e => set("estimated_hours", e.target.value)} type="number" placeholder="e.g. 3" style={inputBase} disabled={locked} /></Fld>
            </div>
          </>
        )}

        {/* ── Section: Mates Gear ── */}
        {showMates && (
          <>
            <SectionHead>Owner &amp; Job Details</SectionHead>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Fld label="Owner Name"><input value={form.owner_name} onChange={e => set("owner_name", e.target.value)} placeholder="e.g. Dave" style={inputBase} disabled={locked} /></Fld>
              <Fld label="Owner Contact"><input value={form.owner_contact} onChange={e => set("owner_contact", e.target.value)} placeholder="e.g. 021 123 4567" style={inputBase} disabled={locked} /></Fld>
            </div>
            <Fld label="Job Description"><textarea value={form.job_description} onChange={e => set("job_description", e.target.value)} placeholder="What needs doing…" rows={3} style={{ ...inputBase, resize: "vertical" }} disabled={locked} /></Fld>
          </>
        )}

      </div>{/* end locked wrapper */}

      {saveError && <div style={{ marginBottom: 14, padding: "10px 14px", background: STATIC.redBg, border: `1px solid ${STATIC.redBorder}`, borderRadius: 8, fontSize: 13, color: STATIC.red }}>{saveError}</div>}

      <button onClick={handleSave} disabled={!form.model.trim() || isSaving || uploading} style={{
        width: "100%", padding: "14px", borderRadius: 8, border: "none",
        background: form.model.trim() && !isSaving && !uploading ? (C?.goldDim || "#C24E10") : "#222",
        color: form.model.trim() && !isSaving && !uploading ? "#fff" : STATIC.dim,
        fontFamily: "inherit", fontWeight: 600, fontSize: 15,
        cursor: form.model.trim() && !isSaving && !uploading ? "pointer" : "not-allowed",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
      }}>
        {isSaving ? (
          <>
            <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.2)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "aiSpin 0.7s linear infinite" }} />
            Saving…
          </>
        ) : uploading ? "Uploading…" : `Save ${typeLabel[form.instrument_type] || "Instrument"}`}
      </button>
      <style>{`@keyframes aiSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
